import React from "react";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";

import Panel from "ui/Panel";
import Loading from "ui/Loading";

import {
  VacuumHistory as VacuumHistoryType,
  VacuumHistoryVariables,
  VacuumHistory_getVacuumRuns_vacuumRuns,
} from "./types/VacuumHistory";
import { useDateRange } from "providers/WithDateRange";
import { useRoutes } from "utils/routes";
import QUERY from "./Query.graphql";
import { makeFilter } from "utils/filter";
import Grid, { GridColumn } from "ui/Grid";
import { useQueryStringSearch } from "ui/QueryStringSearch";

type VacuumRun = VacuumHistory_getVacuumRuns_vacuumRuns & {
  dbName: string;
  postgresRoleName?: string;
  schemaName?: string;
  tableName?: string;
  duration: number;
};

const VacuumHistory: React.FunctionComponent<{
  serverId: string;
  databaseId?: string;
}> = ({ serverId, databaseId }) => {
  const { serverRole, serverVacuum } = useRoutes();
  const { from, to } = useDateRange();
  const [searchTerm, SearchInput] = useQueryStringSearch("q");
  const { data, loading, error } = useQuery<
    VacuumHistoryType,
    VacuumHistoryVariables
  >(QUERY, {
    variables: {
      serverId,
      databaseId,
      startTs: from.unix(),
      endTs: to.unix(),
    },
  });
  if (loading || error) {
    return (
      <Panel title="History">
        <Loading error={!!error} />
      </Panel>
    );
  }

  // We already tell the server to only send us finished vacuums, but ensure this here
  const vacuumRuns: VacuumRun[] = data.getVacuumRuns.vacuumRuns
    .filter((v) => !!v.vacuumEnd)
    .map((v) => ({
      dbName: v.databaseName,
      postgresRoleName: v.postgresRole?.name,
      schemaName: v.schemaTable?.schemaName ?? "unknown schema",
      tableName: v.schemaTable?.tableName ?? "unknown table",
      duration: v.vacuumEnd - v.vacuumStart,
      ...v,
    }));

  const gridData = vacuumRuns.filter(
    makeFilter(searchTerm, "postgresRoleName", "schemaName", "tableName"),
  );

  const columns: GridColumn<
    (typeof gridData)[number],
    keyof (typeof gridData)[number]
  >[] = [
    {
      field: "dbName",
      header: "Database",
      width: !databaseId ? "100px" : false,
    },
    {
      field: "schemaName",
      header: "Schema",
      width: "100px",
    },
    {
      field: "tableName",
      header: "Table",
      width: "minmax(20%,1fr)",
      renderer: function TableCell({ rowData, fieldData }) {
        return (
          <span>
            <Link to={serverVacuum(serverId, rowData.identity)}>
              {fieldData}
            </Link>
            {rowData.toast && " (TOAST)"}
          </span>
        );
      },
    },
    {
      field: "duration",
      header: "Duration",
      width: "min(15%,120px)",
      renderer: function DurationCell({ fieldData }) {
        return moment.duration(fieldData * 1000).humanize();
      },
      defaultSortOrder: "DESC",
    },
    {
      field: "vacuumEnd",
      header: "Finished",
      width: "min(20%,220px)",
      renderer: function VacuumEndCell({ fieldData }) {
        return moment.unix(fieldData).format("ll LTS z");
      },
      defaultSortOrder: "DESC",
    },
    {
      field: "postgresRoleName",
      header: "Role / Autovacuum",
      width: "min(10%,140px)",
      renderer: function RoleCell({ rowData, fieldData }) {
        if (fieldData && rowData.postgresRole) {
          return (
            <Link to={serverRole(serverId, rowData.postgresRole.id)}>
              {fieldData}
            </Link>
          );
        } else if (rowData.autovacuum) {
          return "autovacuum";
        } else {
          return "-";
        }
      },
    },
  ];

  return (
    <Panel title="History" secondaryTitle={<SearchInput />}>
      <Grid
        striped
        defaultSortBy="vacuumEnd"
        data={gridData}
        columns={columns}
        pageSize={20}
      />
    </Panel>
  );
};

export default VacuumHistory;
